import { useEffect, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface OrgUser {
  user_id: string;
  name: string | null;
  email: string;
}

interface UserSelectorProps {
  value?: string;
  onChange: (userId: string | undefined) => void;
  label?: string;
  placeholder?: string;
  allowUnassigned?: boolean;
  disabled?: boolean;
  className?: string;
}

export function UserSelector({
  value,
  onChange,
  label = "Owner",
  placeholder = "Select owner",
  allowUnassigned = true,
  disabled = false,
  className,
}: UserSelectorProps) {
  const { profile } = useAuth();
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadOrgUsers = async () => {
      if (!profile?.org_id) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('user_id, name, email')
          .eq('org_id', profile.org_id)
          .order('name');

        if (error) throw error;

        setUsers(data || []);
      } catch (error) {
        console.error('Error loading org users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadOrgUsers();
  }, [profile?.org_id]);

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}
      <Select
        value={value || 'unassigned'}
        onValueChange={(val) => onChange(val === 'unassigned' ? undefined : val)}
        disabled={disabled || isLoading}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {allowUnassigned && (
            <SelectItem value="unassigned">Unassigned</SelectItem>
          )}
          {users.map((user) => (
            <SelectItem key={user.user_id} value={user.user_id}>
              {user.name || user.email} {user.name && `(${user.email})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
